import { isEmailValid } from "ui/components/TextField/EmailTextField/emailUtils";
import * as yup from "yup";

export const IGNORE_ERROR = "IGNORE";

export const EMAIL_EXISTS_ERROR = "Korisnik s ovom email adresom već postoji.";



export const USER_CREATE_FORM_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test(
      "valid-email",
      "Neispravna email adresa",
      (value) => {
        if (!value) return true;
        return isEmailValid(value);
      }
    ),
  userName: yup.string().required('Molimo Vas da unesete korisničko ime'),
  password: yup.string().required('Molimo Vas da unesete korisničku lozinku'),
  confirmPassword: yup
    .string()
    .required('Molimo Vas da ponovite lozinku')
    .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
  roles: yup
    .array()
    .of(yup.string())
    .test(
      'at-least-one-role',
      'Molimo Vas da odaberete barem jednu rolu',
      (value) => Array.isArray(value) && value.length > 0
    ),
}, [['phoneNumber', 'mobilePhoneNumber']]);

export const USER_EDIT_FORM_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test(
      "valid-email",
      "Neispravna email adresa",
      (value) => {
        if (!value) return true;
        return isEmailValid(value);
      }
    ),
  userName: yup.string().required('Molimo Vas da unesete korisničko ime'),
  password: yup.string().nullable().notRequired(),
  confirmPassword: yup
    .string()
    .nullable()
    .when('password', {
      is: (val: string | null | undefined) => !!val && val.length > 0,
      then: (schema) =>
        schema
          .required('Molimo Vas da ponovite lozinku')
          .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
      otherwise: (schema) => schema.notRequired(),
    }),
  roles: yup
    .array()
    .of(yup.string())
    .test(
      'at-least-one-role',
      'Molimo Vas da odaberete barem jednu rolu',
      (value) => Array.isArray(value) && value.length > 0
    ),
}, [['phoneNumber', 'mobilePhoneNumber']]);


/*---------------------------------------------------------------------------------------
  PO-KORAK SHEME (navigacija "Naprijed")
-----------------------------------------------------------------------------------------*/

// Korak 1 — Osnovni podaci
export const USER_INFO_STEP_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
});

// Zajednička pravila za rolu (korak 2)
const rolesRule = yup
  .array()
  .of(yup.string())
  .test(
    'at-least-one-role',
    'Molimo Vas da odaberete barem jednu rolu',
    (value) => Array.isArray(value) && value.length > 0
  );

// Korak 2 — Korisnički podaci (kreiranje: lozinka obavezna)
export const USER_ACCOUNT_STEP_CREATE_SCHEMA = yup.object().shape({
  userName: yup.string().required('Molimo Vas da unesete korisničko ime'),
  password: yup.string().required('Molimo Vas da unesete korisničku lozinku'),
  confirmPassword: yup
    .string()
    .required('Molimo Vas da ponovite lozinku')
    .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
  roles: rolesRule,
});

// Korak 2 — Korisnički podaci (uređivanje: lozinka opcionalna)
export const USER_ACCOUNT_STEP_EDIT_SCHEMA = yup.object().shape({
  userName: yup.string().required('Molimo Vas da unesete korisničko ime'),
  password: yup.string().nullable().notRequired(),
  confirmPassword: yup
    .string()
    .nullable()
    .when('password', {
      is: (val: string | null | undefined) => !!val && val.length > 0,
      then: (schema) =>
        schema
          .required('Molimo Vas da ponovite lozinku')
          .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
      otherwise: (schema) => schema.notRequired(),
    }),
  roles: rolesRule,
});

// Korak 3 — Kontakt (email obavezan; telefon ili mobitel — barem jedan)
export const USER_CONTACT_STEP_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test('valid-email', 'Neispravna email adresa', (value) => {
      if (!value) return true;
      return isEmailValid(value);
    }),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) =>
        schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) =>
        schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
}, [['phoneNumber', 'mobilePhoneNumber']]);
